import { Typography } from '@mui/material';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Attachment } from 'sr-types/lib/production/v1/graphql';
import RoundAvatar, { AvatarSizes } from '../../production/header/RoundAvatar';
import { H, V } from '../Layout';
import { useStyling } from '../Theme';
import { EntitySecurityProvider, SECURITY_ACTIONS } from '../auth/Security';
import { UserContext } from '../auth/UserContext';
import type { SecuredActionType } from '../components/ActionType';
import EllipsisText from '../components/EllipsisText';
import Loading from '../components/Loading';
import ResponsiveActions from '../components/ResponsiveActions';
import TagsPreview from '../components/TagsPreview';
import { fetchAttachmentUrl } from '../components/attachments';
import { ENTITY_CONSTANTS } from '../constants/entityConstants';
import { useBrowserOrAccountLocale } from '../hooks/useBrowserOrAccountLocale';
import { useI18n } from '../i18n/I18n';
import { Icons } from '../icons/Icons';
import { getSafeExternalUrl } from '../links/linkUtils';
import { toDisplay } from '../utils/dateTime';
import download from '../utils/download';
import AttachmentsImagePreview from './AttachmentsImagePreview';
import { convertBytesToMbsOrKbs, imageExtensions } from './attachmentsApi';

type AttachmentsProps = {
    file: Attachment;
    onDelete?: () => void;
    onEdit?: () => void;
    onShare?: () => void;
    isPreview?: boolean;
    hideAction?: boolean;
    hasEntityCommentPermission?: boolean;
};

export default function AttachmentsRenderer({
    file,
    onDelete,
    onEdit,
    onShare,
    isPreview = false,
    hideAction = false,
    hasEntityCommentPermission = false
}: AttachmentsProps) {
    const { theme, isDarkMode } = useStyling();
    const { activeOrganizationAccount } = useContext(UserContext);
    const [isHover, setIsHover] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const deleteLabel = useI18n('actions.delete');
    const editLabel = useI18n('actions.edit');
    const previewLabel = useI18n('preview.label');
    const downloadLabel = useI18n('actions.download');
    const shareLabel = useI18n('actions.share');
    const locale = useBrowserOrAccountLocale();

    const handlePreviewClick = useCallback(() => {
        if (!imageExtensions.includes(file?.type)) {
            if (file?.linkUrl) {
                const safeUrl = getSafeExternalUrl(file?.linkUrl);
                if (safeUrl) {
                    window.open(safeUrl, '_blank', 'noopener,noreferrer');
                }
                return;
            }
            if (!previewUrl) {
                setLoading(true);
                fetchAttachmentUrl(file?.entityReference?.id, activeOrganizationAccount)
                    .then((url) => {
                        if (url) {
                            window.open(url, '_blank', 'noopener,noreferrer');
                        }
                        setPreviewUrl(url);
                        setLoading(false);
                    })
                    .catch((err) => {
                        setLoading(false);
                    });
            } else {
                window.open(previewUrl, '_blank', 'noopener,noreferrer');
            }
        } else {
            setIsPreviewOpen(true);
        }
    }, [activeOrganizationAccount, file?.entityReference?.id, file?.linkUrl, file?.type, previewUrl]);

    const securedActions = useMemo<SecuredActionType[]>(() => {
        const actions: SecuredActionType[] = [];

        if (hasEntityCommentPermission) {
            if (onDelete) {
                actions.push({
                    securityAction: SECURITY_ACTIONS.DELETE,
                    entityName: ENTITY_CONSTANTS.attachment,
                    icon: Icons.Delete,
                    onClick: onDelete,
                    label: deleteLabel
                });
            }

            if (!isPreview && onEdit) {
                actions.push({
                    securityAction: SECURITY_ACTIONS.EDIT,
                    entityName: ENTITY_CONSTANTS.attachment,
                    icon: Icons.Edit,
                    label: editLabel,
                    onClick: onEdit
                });
            }
        }

        actions.push({
            securityAction: SECURITY_ACTIONS.READ,
            entityName: ENTITY_CONSTANTS.attachment,
            icon: Icons.Visibility,
            onClick: handlePreviewClick,
            label: previewLabel
        });

        if (onShare) {
            actions.push({
                securityAction: SECURITY_ACTIONS.SHARE,
                entityName: ENTITY_CONSTANTS.attachment,
                icon: Icons.Share,
                onClick: onShare,
                label: shareLabel
            });
        }

        if (!isPreview && !file?.linkUrl) {
            actions.push({
                securityAction: SECURITY_ACTIONS.READ,
                entityName: ENTITY_CONSTANTS.attachment,
                icon: Icons.Download,
                label: downloadLabel,
                onClick: () => {
                    download(file.entityReference.id, file.name, activeOrganizationAccount, undefined);
                }
            });
        }

        return actions;
    }, [
        activeOrganizationAccount,
        deleteLabel,
        downloadLabel,
        editLabel,
        file?.entityReference?.id,
        file?.linkUrl,
        file.name,
        handlePreviewClick,
        hasEntityCommentPermission,
        isPreview,
        onDelete,
        onEdit,
        onShare,
        previewLabel,
        shareLabel
    ]);

    return (
        <EntitySecurityProvider entityName={ENTITY_CONSTANTS.attachment} entitySecurity={file?.security ?? {}}>
            <V
                fill
                gap={1}
                sx={{
                    p: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    '&:hover': {
                        backgroundColor: theme.palette.grey[isDarkMode ? 700 : 100]
                    }
                }}
            >
                <H
                    fill
                    sx={{ gap: 2, flexGrow: 1, position: 'relative', cursor: 'pointer' }}
                    onMouseEnter={() => setIsHover(true)}
                    onMouseLeave={() => setIsHover(false)}
                    onClick={(event) => {
                        if (file?.isLink) {
                            event.stopPropagation();
                            handlePreviewClick();
                            return;
                        }
                        setIsPreviewOpen(true);
                    }}
                >
                    <RoundAvatar
                        imageId={imageExtensions.includes(file.type) ? file.entityReference.id : undefined}
                        size={AvatarSizes.md}
                        title={file.name}
                        bgColor={theme.palette.divider}
                        Icon={file?.isLink ? Icons.Link : Icons.AttachFileOutlined}
                        iconSx={{ color: theme.palette.text.secondary }}
                    />
                    <V grow>
                        <EllipsisText text={file.name} breakAll />
                        <H sx={{ gap: 1, mt: 1, width: 'fit-content' }}>
                            {file?.size ? (
                                <Typography
                                    fontSize={'x-small'}
                                    fontWeight={'normal'}
                                    color={theme.palette.text.disabled}
                                >
                                    {convertBytesToMbsOrKbs(file.size)}
                                </Typography>
                            ) : null}
                            {file.createdOn && (
                                <Typography
                                    fontSize={'x-small'}
                                    fontWeight={'normal'}
                                    color={theme.palette.text.disabled}
                                >
                                    {toDisplay(file.createdOn, false, locale)}
                                </Typography>
                            )}
                            {file?.type && <EllipsisText text={`.${file.type}`} sx={{ color: 'text.disabled' }} />}
                        </H>
                    </V>

                    <H
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                            justifyContent: 'flex-end',
                            width: 0,
                            opacity: isHover ? 1 : 0,
                            pointerEvents: isHover ? 'auto' : 'none'
                        }}
                    >
                        {loading ? (
                            <Loading />
                        ) : hideAction ? null : (
                            <ResponsiveActions securedActions={securedActions} showBorder isActivated={isHover} />
                        )}
                    </H>
                </H>
                <V onClick={(event) => event.stopPropagation()}>
                    <TagsPreview tags={file?.tags ?? []} />
                </V>
                {imageExtensions.includes(file?.type) && isPreviewOpen && (
                    <AttachmentsImagePreview
                        isPreviewOpen={isPreviewOpen}
                        setIsPreviewOpen={setIsPreviewOpen}
                        name={file?.name}
                        id={file?.entityReference?.id}
                    />
                )}
            </V>
        </EntitySecurityProvider>
    );
}
